// OmniSearch + OmniSearchTrigger hooks.
// Communicate via custom DOM event on #omnisearch-modal — no shared mutable state.

import * as OmniSearchEngine from "../omnisearch";
import type {
  LiveViewHook,
  OmnisearchPushEventWire,
  SearchPushEventWire,
} from "./types";

type OmniSearchHookLike = LiveViewHook<
  OmnisearchPushEventWire | SearchPushEventWire
>;
import {
  activateModalA11y,
  hasActiveModal,
  keepModalFocus,
  modalOpen,
  modalClose,
  modalCleanup,
  restoreModalFocus,
  syncModalShellState,
  trapModalFocus,
} from "./modal";
// Element ids are dox-owned (`OmnisearchElementId` union in `ui/ci.dox`)
// so a wire-string rename upstream surfaces here as a tsc error rather
// than a silent `null` from `getElementById`. `validOmnisearchElementId`
// is the wire-form (JSON) validator — the runtime returns its input, but
// the type union pins the spelling. Illumination is for human-readable
// labels, not on-the-wire ids that have to match the Elixir-rendered DOM.
import {
  illuminateCssClassModal,
  validOmnisearchElementId,
  validOmnisearchCategory,
  type OmnisearchCategory,
} from "../../../.dox/ui";

const APP_SHELL = illuminateCssClassModal("appShell");

const CLOSE_EVENT = "omnisearch:close" as const;

// `clampIndex`, `searchKeyAction`, and `SearchKeyResult` live in
// ./keyboard-actions alongside the code-browser decoder. Re-export so existing
// imports of these symbols from "./search" keep resolving.
export {
  clampIndex,
  searchKeyAction,
  type SearchKeyResult,
} from "./keyboard-actions";
import { searchKeyAction } from "./keyboard-actions";

// Pure: prefetch a URL via <link rel="prefetch">
const injectPrefetch = (url: string): void => {
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.href = url;
  document.head.appendChild(link);
};

// Shared with the branch picker (./branch_select), which renders the same
// kind of result list.
export const highlightItem = (
  items: NodeListOf<HTMLElement>,
  idx: number,
): void => {
  items.forEach((item, i) => {
    if (i === idx) {
      item.classList.add("bg-surface");
      item.scrollIntoView({ block: "nearest" });
    } else {
      item.classList.remove("bg-surface");
    }
  });
};

// --- Interfaces ---

interface OmniSearchTriggerHook extends OmniSearchHookLike {
  _keyHandler: (e: KeyboardEvent) => void;
}

interface OmniSearchHook extends OmniSearchHookLike {
  _selected: number;
  _debounceTimer: ReturnType<typeof setTimeout> | null;
  _activeTab: OmnisearchCategory | null;
  _allResults: readonly unknown[];
  _closing: boolean;
  _animatedClose: (url?: string) => void;
  _docEscapeHandler: (e: KeyboardEvent) => void;
  _focusTrapHandler: (e: KeyboardEvent) => void;
  _focusGuardHandler: (e: FocusEvent) => void;
  _restoreFocusEl: HTMLElement | null;
  _doSearch(query: string): void;
  _renderWithTab(tabsEl: Element | null, resultsEl: Element): void;
}

// --- Setup helpers ---

const makeAnimatedClose = (
  hook: OmniSearchHook,
): ((navigateUrl?: string) => void) => {
  return (navigateUrl?: string): void => {
    if (hook._closing) return;
    hook._closing = true;
    if (navigateUrl) injectPrefetch(navigateUrl);

    modalClose(hook.el, () => {
      document.body.style.overflowX = "";
      hook.pushEvent(
        navigateUrl ? "omnisearchNavigate" : "omnisearchClose",
        navigateUrl ? { url: navigateUrl } : {},
      );
    });
  };
};

// Run `search` once the input has been still for `delayMs`. The pending
// timer lives on the owner, so the owner can cancel it (destroyed, a tab
// click) or see that one is pending; it is null again once it fires.
// Shared with the branch picker (./branch_select).
export const bindDebouncedInput = (
  input: HTMLInputElement,
  owner: { _debounceTimer: ReturnType<typeof setTimeout> | null },
  search: (query: string) => void,
  delayMs: number = 120,
): void => {
  input.addEventListener("input", () => {
    if (owner._debounceTimer) clearTimeout(owner._debounceTimer);
    owner._debounceTimer = setTimeout(() => {
      owner._debounceTimer = null;
      search(input.value);
    }, delayMs);
  });
};

const bindKeyboardNav = (
  el: HTMLElement,
  hook: OmniSearchHook,
  close: (url?: string) => void,
): void => {
  el.addEventListener("keydown", (e: KeyboardEvent) => {
    const items = el.querySelectorAll<HTMLElement>("[data-search-result]");
    const result = searchKeyAction(
      e.key,
      hook._selected,
      items.length,
      items[hook._selected]?.dataset["url"],
    );

    switch (result.action) {
      case "select":
        e.preventDefault();
        hook._selected = result.index;
        highlightItem(items, result.index);
        break;
      case "navigate":
        e.preventDefault();
        close(result.url);
        break;
      case "close":
        e.preventDefault();
        close();
        break;
    }
  });
};

// --- Hooks ---

export const OmniSearchTrigger = {
  mounted(this: OmniSearchTriggerHook) {
    this._keyHandler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        const modal = document.getElementById(
          validOmnisearchElementId("omnisearchModal"),
        );
        if (modal) {
          modal.dispatchEvent(new CustomEvent(CLOSE_EVENT));
        } else if (hasActiveModal()) {
          return;
        } else {
          this.pushEvent("omnisearchToggle", {});
        }
      }
    };
    document.addEventListener("keydown", this._keyHandler);
  },
  destroyed(this: OmniSearchTriggerHook) {
    document.removeEventListener("keydown", this._keyHandler);
  },
};

export const OmniSearch = {
  mounted(this: OmniSearchHook) {
    this._selected = 0;
    this._debounceTimer = null;
    this._activeTab = null;
    this._allResults = [];
    this._closing = false;

    const input = this.el.querySelector("input");
    const panel = this.el.querySelector<HTMLElement>(".modal-panel");
    const shell = document.querySelector<HTMLElement>(`.${APP_SHELL}`);

    this._restoreFocusEl = activateModalA11y(this.el, panel);
    modalOpen(shell, panel);

    this.handleEvent("searchData", (payload: unknown) => {
      const { items } = payload as { items?: unknown[] };
      OmniSearchEngine.load(
        (items || []) as Parameters<typeof OmniSearchEngine.load>[0],
      );
    });

    if (input)
      bindDebouncedInput(input, this, (query) => {
        this._activeTab = null;
        this._doSearch(query);
      });

    const animatedClose = makeAnimatedClose(this);
    this._animatedClose = animatedClose;

    this.el.addEventListener(CLOSE_EVENT, () => {
      animatedClose();
    });

    const backdrop = this.el.querySelector(".modal-backdrop");
    if (backdrop)
      backdrop.addEventListener("click", (e: Event) => {
        e.stopPropagation();
        animatedClose();
      });

    const docEscapeHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        animatedClose();
      }
    };
    document.addEventListener("keydown", docEscapeHandler);
    this._docEscapeHandler = docEscapeHandler;

    this._focusTrapHandler = (e: KeyboardEvent): void => {
      trapModalFocus(e, this.el, panel);
    };
    this.el.addEventListener("keydown", this._focusTrapHandler);

    this._focusGuardHandler = (e: FocusEvent): void => {
      keepModalFocus(this.el, panel, e.target);
    };
    document.addEventListener("focusin", this._focusGuardHandler);

    bindKeyboardNav(this.el, this, animatedClose);
  },

  updated(this: OmniSearchHook) {
    const input = this.el.querySelector("input");
    if (input && document.activeElement !== input) input.focus();
    syncModalShellState();
  },

  destroyed(this: OmniSearchHook) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._closing = false;
    document.removeEventListener("keydown", this._docEscapeHandler);
    this.el.removeEventListener("keydown", this._focusTrapHandler);
    document.removeEventListener("focusin", this._focusGuardHandler);
    modalCleanup();
    restoreModalFocus(this._restoreFocusEl);
  },

  _doSearch(this: OmniSearchHook, query: string) {
    const tabsEl = this.el.querySelector(
      `#${validOmnisearchElementId("omnisearchTabs")}`,
    );
    const resultsEl = this.el.querySelector(
      `#${validOmnisearchElementId("omnisearchResults")}`,
    );
    if (!resultsEl) return;

    if (!query.trim()) {
      if (tabsEl) tabsEl.innerHTML = "";
      resultsEl.innerHTML = OmniSearchEngine.renderEmpty();
      this._allResults = [];
      this._selected = 0;
      return;
    }

    this._allResults = OmniSearchEngine.searchItems(query);
    this._renderWithTab(tabsEl, resultsEl);
  },

  _renderWithTab(
    this: OmniSearchHook,
    tabsEl: Element | null,
    resultsEl: Element,
  ) {
    const filtered = OmniSearchEngine.filterByTab(
      this._allResults as Parameters<typeof OmniSearchEngine.filterByTab>[0],
      this._activeTab,
    );

    if (tabsEl) {
      tabsEl.innerHTML = OmniSearchEngine.renderTabs(
        this._activeTab,
        this._allResults as Parameters<typeof OmniSearchEngine.renderTabs>[1],
      );
      tabsEl
        .querySelectorAll<HTMLElement>("[data-search-tab]")
        .forEach((btn) => {
          btn.addEventListener("click", (e: Event) => {
            e.preventDefault();
            // A tab click during the 120ms input-debounce window must
            // cancel the pending fire — the debounced search resets
            // _activeTab to null, which used to silently discard the tab
            // the user had just chosen.
            if (this._debounceTimer) clearTimeout(this._debounceTimer);
            this._debounceTimer = null;
            const tab = btn.dataset["searchTab"];
            // Bless the wire tag through the dox-emitted validator so a
            // mistyped category in the rendered tab markup fails loudly here
            // instead of silently filtering to an empty result set.
            this._activeTab =
              tab === "" || tab == null ? null : validOmnisearchCategory(tab);
            this._selected = 0;
            this._renderWithTab(tabsEl, resultsEl);
          });
        });
    }

    resultsEl.innerHTML = OmniSearchEngine.renderResults(filtered);
    this._selected = 0;

    const items = resultsEl.querySelectorAll<HTMLElement>(
      "[data-search-result]",
    );
    highlightItem(items, 0);

    items.forEach((el) => {
      el.addEventListener("click", (e: Event) => {
        e.preventDefault();
        const url = el.dataset["url"];
        if (url) this._animatedClose(url);
      });
    });
  },
};
